import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import { Card, CardHeader, CardBody, CardFooter, Container, Row, Button, Input, Label, Col } from "reactstrap";
import { useQuery } from "react-query";
import { errorToast, successToast } from "Components/Toasts";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import Layout from "HorizontalMenu/Menu";
import Navbar from "HorizontalMenu/Navbar";
import apiGlobal from "global/api.global";
import { queryKeyes } from "shared/queryKeys";
import { dateTimeFormat, errResponse } from "GenericForms/Helper";
import { Get, loadReportingEventsMaster } from "VesselMaster/vesselMaster.hooks";
import ErrorComponent from "Components/ErrorComponent";
import ErrorTooltip from "Components/ErrorTooltip";
import env from "environment_system/env_system";
import Select from "react-select";
import React, { useState } from "react";
import PageLoading from "Components/PageLoading";
import { customStyle } from "shared/CommonCSS";
import { queryClient } from "react-query/queryClient";
import AsyncSelect from "react-select/async";
import { GroupBase } from 'react-select';
import { commonValidationMessages } from "Components/ValidationErrorMessages";

const OrganizationConfiguration = () => {
    const [refreshKey, setRefreshKey] = useState(0);

    const { data: organizationConfiguration, isLoading, isError } = useQuery(
        [queryKeyes.organization.OrganizationConfiguration.key],
        async () => await Get(queryKeyes.organization.OrganizationConfiguration.url()),
        { staleTime: Infinity }
    );

    const getInitialValues = () => {
        if (organizationConfiguration && organizationConfiguration) {
            const config = organizationConfiguration;
            return {
                id: config.id || null,
                organisation_name: config.organisation_name || "",
                default_password_policy: config.default_password_policy || "random",
                default_password: config.default_password || "",
                track_old_password: config.track_old_password || false,
                is_offline_available: config.is_offline_available || false,
                send_email_status: config.send_email_status || false,
                eu_voyage_start_event: config.eu_voyage_start_event
                    ? {
                        id: config.eu_voyage_start_event,
                        reporting_event_name: config.eu_voyage_start_event_name,
                    }
                    : null,
                eu_voyage_end_event: config.eu_voyage_end_event
                    ? {
                        id: config.eu_voyage_end_event,
                        reporting_event_name: config.eu_voyage_end_event_name,
                    }
                    : null,
                eu_port_start_event: config.eu_port_start_event
                    ? {
                        id: config.eu_port_start_event,
                        reporting_event_name: config.eu_port_start_event_name,
                    }
                    : null,
                eu_port_end_event: config.eu_port_end_event
                    ? {
                        id: config.eu_port_end_event,
                        reporting_event_name: config.eu_port_end_event_name,
                    }
                    : null,
                is_system_under_maintenance: config.is_system_under_maintenance ?? false,
                maintenance_start_time: config.maintenance_start_time ?? null,
                maintenance_end_time: config.maintenance_end_time ?? null,
            };
        }
        // Default initial values for new record
        return {
            organisation_name: "",
            default_password_policy: "random",
            default_password: "",
            track_old_password: false,
            is_offline_available: false,
            send_email_status: false,
            eu_voyage_start_event: null as any,
            eu_voyage_end_event: null as any,
            eu_port_start_event: null as any,
            eu_port_end_event: null as any,
            is_system_under_maintenance: false,
            maintenance_start_time: null as any,
            maintenance_end_time: null as any,
        };
    };

    const formik: FormikProps<any> = useFormik({
        enableReinitialize: true,
        initialValues: getInitialValues(),
        validationSchema: Yup.object({
            organisation_name: Yup.string().nullable().required("Organization name is required"),
            default_password_policy: Yup.string().oneOf(["random", "default"]),
            default_password: Yup.string()
                .nullable()
                .when("default_password_policy", {
                    is: "default",
                    then: (schema) =>
                        schema
                            .matches(
                                /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/,
                                "Password must be 8+ characters with letters, numbers & symbols"
                            )
                            .required("Please provide a default password"),
                }),
            track_old_password: Yup.boolean(),
            is_offline_available: Yup.boolean(),
            send_email_status: Yup.boolean(),
            eu_voyage_start_event: Yup.object().nullable().required("Voyage Start Event is required"),
            eu_voyage_end_event: Yup.object().nullable().required("Voyage End Event is required"),
            eu_port_start_event: Yup.object().nullable().required("Port Start Event is required"),
            eu_port_end_event: Yup.object().nullable().required("Port End Event is required"),
            is_system_under_maintenance: Yup.boolean(),
            maintenance_start_time: Yup.date().when('is_system_under_maintenance', {
                is: true,
                then: (schema) => schema.required(commonValidationMessages.required),
                otherwise: (schema) => schema.nullable(),
            }),
            maintenance_end_time: Yup.date().when('is_system_under_maintenance', {
                is: true,
                then: (schema) => schema.required(commonValidationMessages.required),
                otherwise: (schema) => schema.nullable(),
            }),
        })
            .test("unique-events", undefined, function (this, value) {
                const values = value as any;
                if (!values) return true;
                const now = new Date();
                const start = new Date(values.maintenance_start_time);
                const end = new Date(values.maintenance_end_time);
                const errors: Yup.ValidationError[] = [];
                if (values.is_system_under_maintenance) {
                    if (end.getTime() < start.getTime()) {
                        errors.push(
                            new Yup.ValidationError(
                                "Please enter a date time after maintenance start time.",
                                null,
                                "maintenance_end_time"
                            )
                        );
                    }
                    if (start.getTime() < now.getTime()) {
                        errors.push(
                            new Yup.ValidationError(
                                `Please enter a date time after current time.`,
                                null,
                                "maintenance_start_time"
                            )
                        );
                    }
                }
                const getId = (v: any) => v?.id ?? null;
                const fields = {
                    eu_voyage_start_event: getId(values.eu_voyage_start_event),
                    eu_voyage_end_event: getId(values.eu_voyage_end_event),
                    eu_port_start_event: getId(values.eu_port_start_event),
                    eu_port_end_event: getId(values.eu_port_end_event),
                };
                const ids = Object.values(fields).filter(Boolean);
                const duplicates = ids.filter((id, i) => ids.indexOf(id) !== i);
                if (duplicates.length > 0) {
                    const dupIds = new Set(duplicates);
                    for (const [key, id] of Object.entries(fields)) {
                        if (dupIds.has(id)) {
                            errors.push(
                                new Yup.ValidationError(
                                    "This event is already selected in another field.",
                                    null,
                                    key
                                )
                            );
                        }
                    }
                }
                if (errors.length > 0) {
                    return new Yup.ValidationError(errors);
                }
                return true;
            }),
        onSubmit: async (values, actions) => {
            try {
                const payload = {
                    ...values,
                    eu_voyage_start_event: values.eu_voyage_start_event?.id || null,
                    eu_voyage_end_event: values.eu_voyage_end_event?.id || null,
                    eu_port_start_event: values.eu_port_start_event?.id || null,
                    eu_port_end_event: values.eu_port_end_event?.id || null,
                };
                if (!values.id) delete payload.id;
                const isUpdate = Boolean(values?.id as any);
                const apiCall = isUpdate
                    ? apiGlobal.put(`${queryKeyes.organization.OrganizationConfiguration.url()}${values?.id}/`, payload)
                    : apiGlobal.post(queryKeyes.organization.OrganizationConfiguration.url(), payload);

                const res = await apiCall;
                if (res.status === 200 || res.status === 201) {
                    await queryClient.invalidateQueries(queryKeyes.organization.OrganizationConfiguration.key);
                    successToast("Data saved successfully!");
                    setRefreshKey((prev) => prev + 1);
                }
            } catch (err: any) {
                if (errResponse.includes(err?.response?.status)) {
                    errorToast("Internal error occurred, please contact the admin");
                }
            } finally {
                actions.setSubmitting(false);
            }
        },
    });

    const passwordPolicyOptions = [
        { label: "Random", value: "random" },
        { label: "Default", value: "default" },
    ];

    // console.log(new Date(dateTimeFormat(dateTimeFormat(new Date().toLocaleString()))));
    return (
        <React.Fragment>
            <Layout>
                <Navbar />
            </Layout>
            <div className="page-content">
                <Container fluid>
                    <Card>
                        {isLoading && <PageLoading />}
                        {isError && <ErrorComponent message="Failed to load organization configuration." />}
                        {!isLoading && !isError && (
                            <form onSubmit={formik.handleSubmit} autoComplete="off" key={refreshKey}>
                                <CardHeader className="p-2">
                                    <h4 className="mb-0">Organization Configuration</h4>
                                </CardHeader>
                                <CardBody>
                                    <Row className="mb-2">
                                        <Col md={4}>
                                            <Label htmlFor="organisation_name" className="asteric mb-0">Organization Name</Label>
                                            <Input
                                                onFocus={(e) => e.target.select()}
                                                id="organisation_name"
                                                name="organisation_name"
                                                autoComplete="off"
                                                type="text"
                                                value={formik.values.organisation_name || ""}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                className="form-control"
                                            />
                                            {formik.touched.organisation_name && formik.errors.organisation_name && env.form_validation &&
                                                <ErrorTooltip
                                                    target="organisation_name"
                                                    message={formik.errors.organisation_name}
                                                    open={!!formik.errors.organisation_name}
                                                />}
                                        </Col>
                                    </Row>
                                    <Row className="mb-2">
                                        <Col md={4}>
                                            <Label htmlFor="default_password_policy" className="asteric mb-0">Password Policy</Label>
                                            <Select
                                                inputId="default_password_policy"
                                                name="default_password_policy"
                                                options={passwordPolicyOptions}
                                                value={passwordPolicyOptions.find(
                                                    (option) => option.value === formik.values.default_password_policy
                                                )}
                                                onChange={(selectedOption) => {
                                                    formik.setFieldValue("default_password_policy", selectedOption?.value);
                                                }}
                                                classNamePrefix="react-select"
                                                styles={customStyle}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Label htmlFor="default_password" className={`${formik.values.default_password_policy === "default" ? "asteric" : ""} mb-0`}>Default Password</Label>
                                            <Input
                                                onFocus={(e) => e.target.select()}
                                                id="default_password"
                                                name="default_password"
                                                type="text"
                                                className="form-control"
                                                style={{ width: '83%' }}
                                                disabled={formik.values.default_password_policy === "random"}
                                                value={formik.values.default_password || ""}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            {formik.touched.default_password && formik.errors.default_password && env.form_validation &&
                                                <ErrorTooltip
                                                    target="default_password"
                                                    message={formik.errors.default_password}
                                                    open={!!formik.errors.default_password}
                                                />}
                                        </Col>
                                        <Col md={6}>
                                            <div className="form-check mt-1">
                                                <Input
                                                    onFocus={(e) => e.target.select()}
                                                    id="track_old_password"
                                                    name="track_old_password"
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={formik.values.track_old_password}
                                                    onChange={formik.handleChange}
                                                />
                                                <Label htmlFor="track_old_password" className="form-check-label">
                                                    Track Old Password
                                                </Label>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={4}>
                                            <div className="ele_row1">
                                                <Label htmlFor="is_offline_available" className="form-check-label me-2 mb-0 mt-1">
                                                    Is offline functionality available?
                                                </Label>
                                                <div className="square-switch">
                                                    <Input
                                                        type="checkbox"
                                                        switch="none"
                                                        name="is_offline_available"
                                                        id="is_offline_available"
                                                        defaultChecked={formik?.values?.is_offline_available}
                                                        onChange={(e: any) => formik?.setFieldValue("is_offline_available", e.target.checked)}
                                                    />
                                                    <Label
                                                        htmlFor="is_offline_available"
                                                        data-on-label="Yes"
                                                        data-off-label="No"
                                                        className="mt-1"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => {
                                                            if (e.key === " " || e.key === "Enter") {
                                                                e.preventDefault();
                                                                document.getElementById("is_offline_available")?.click();
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="ele_row1">
                                                <Label htmlFor="send_email_status" className="form-check-label me-2 mb-0 mt-1">
                                                    Is email functionality available?
                                                </Label>
                                                <div className="square-switch">
                                                    <Input
                                                        type="checkbox"
                                                        switch="none"
                                                        name="send_email_status"
                                                        id="send_email_status"
                                                        defaultChecked={formik?.values?.send_email_status}
                                                        onChange={(e: any) => formik?.setFieldValue("send_email_status", e.target.checked)}
                                                    />
                                                    <Label
                                                        htmlFor="send_email_status"
                                                        data-on-label="Yes"
                                                        data-off-label="No"
                                                        className="mt-1"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => {
                                                            if (e.key === " " || e.key === "Enter") {
                                                                e.preventDefault();
                                                                document.getElementById("send_email_status")?.click();
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col md={3}>
                                            <Label className="mb-0 asteric" htmlFor="eu_voyage_start_event">EU Voyage Start Event</Label>
                                            <AsyncSelect<{ id: any; name: any; }, false, GroupBase<{ id: any; name: any; }>>
                                                name="eu_voyage_start_event"
                                                cacheOptions
                                                defaultOptions
                                                inputId="eu_voyage_start_event"
                                                loadOptions={async (inputValue: string) => {
                                                    const events: any = await loadReportingEventsMaster();
                                                    if (inputValue) {
                                                        return events.filter((ev: any) =>
                                                            ev?.name?.toLowerCase()?.includes(inputValue.toLowerCase())
                                                        );
                                                    }
                                                    return events;
                                                }}
                                                getOptionLabel={(e: any) => e?.reporting_event_name}
                                                getOptionValue={(e: any) => e?.id}
                                                onChange={(selectedOption) => formik.setFieldValue('eu_voyage_start_event', selectedOption)}
                                                onBlur={() => formik.setFieldTouched('eu_voyage_start_event', true)}
                                                menuPortalTarget={document.body}
                                                styles={customStyle}
                                                value={formik.values.eu_voyage_start_event || null}
                                                noOptionsMessage={() => "Please enter the event name"}
                                            />
                                            {formik.touched.eu_voyage_start_event && formik.errors.eu_voyage_start_event && env.form_validation && (
                                                <ErrorTooltip
                                                    target="eu_voyage_start_event"
                                                    message={formik.errors.eu_voyage_start_event}
                                                    open={!!formik.errors.eu_voyage_start_event}
                                                />
                                            )}
                                        </Col>
                                        <Col md={3}>
                                            <Label className="mb-0 asteric" htmlFor="eu_voyage_end_event">EU Voyage End Event</Label>
                                            <AsyncSelect<{ id: any; name: any; }, false, GroupBase<{ id: any; name: any; }>>
                                                name="eu_voyage_end_event"
                                                cacheOptions
                                                defaultOptions
                                                inputId="eu_voyage_end_event"
                                                loadOptions={async (inputValue: string) => {
                                                    const events: any = await loadReportingEventsMaster();
                                                    if (inputValue) {
                                                        return events.filter((ev: any) =>
                                                            ev?.name?.toLowerCase()?.includes(inputValue.toLowerCase())
                                                        );
                                                    }
                                                    return events;
                                                }}
                                                getOptionLabel={(e: any) => e?.reporting_event_name}
                                                getOptionValue={(e: any) => e?.id}
                                                onChange={(selectedOption) => formik.setFieldValue('eu_voyage_end_event', selectedOption)}
                                                onBlur={() => formik.setFieldTouched('eu_voyage_end_event', true)}
                                                menuPortalTarget={document.body}
                                                styles={customStyle}
                                                value={formik.values.eu_voyage_end_event || null}
                                                noOptionsMessage={() => "Please enter the event name"}
                                            />
                                            {formik.touched.eu_voyage_end_event && formik.errors.eu_voyage_end_event && env.form_validation && (
                                                <ErrorTooltip
                                                    target="eu_voyage_end_event"
                                                    message={formik.errors.eu_voyage_end_event}
                                                    open={!!formik.errors.eu_voyage_end_event}
                                                />
                                            )}
                                        </Col>
                                        <Col md={3}>
                                            <Label className="mb-0 asteric" htmlFor="eu_port_start_event">EU Port Start Event</Label>
                                            <AsyncSelect<{ id: any; name: any; }, false, GroupBase<{ id: any; name: any; }>>
                                                name="eu_port_start_event"
                                                cacheOptions
                                                defaultOptions
                                                inputId="eu_port_start_event"
                                                loadOptions={async (inputValue: string) => {
                                                    const events: any = await loadReportingEventsMaster();
                                                    if (inputValue) {
                                                        return events.filter((ev: any) =>
                                                            ev?.name?.toLowerCase()?.includes(inputValue.toLowerCase())
                                                        );
                                                    }
                                                    return events;
                                                }}
                                                getOptionLabel={(e: any) => e?.reporting_event_name}
                                                getOptionValue={(e: any) => e?.id}
                                                onChange={(selectedOption) => formik.setFieldValue('eu_port_start_event', selectedOption)}
                                                onBlur={() => formik.setFieldTouched('eu_port_start_event', true)}
                                                menuPortalTarget={document.body}
                                                styles={customStyle}
                                                value={formik.values.eu_port_start_event || null}
                                                noOptionsMessage={() => "Please enter the event name"}
                                            />
                                            {formik.touched.eu_port_start_event && formik.errors.eu_port_start_event && env.form_validation && (
                                                <ErrorTooltip
                                                    target="eu_port_start_event"
                                                    message={formik.errors.eu_port_start_event}
                                                    open={!!formik.errors.eu_port_start_event}
                                                />
                                            )}
                                        </Col>
                                        <Col md={3}>
                                            <Label className="mb-0 asteric" htmlFor="eu_port_end_event">EU Port End Event</Label>
                                            <AsyncSelect<{ id: any; name: any; }, false, GroupBase<{ id: any; name: any; }>>
                                                name="eu_port_end_event"
                                                cacheOptions
                                                defaultOptions
                                                inputId="eu_port_end_event"
                                                loadOptions={async (inputValue: string) => {
                                                    const events: any = await loadReportingEventsMaster();
                                                    if (inputValue) {
                                                        return events.filter((ev: any) =>
                                                            ev?.name?.toLowerCase()?.includes(inputValue.toLowerCase())
                                                        );
                                                    }
                                                    return events;
                                                }}
                                                getOptionLabel={(e: any) => e?.reporting_event_name}
                                                getOptionValue={(e: any) => e?.id}
                                                onChange={(selectedOption) => formik.setFieldValue('eu_port_end_event', selectedOption)}
                                                onBlur={() => formik.setFieldTouched('eu_port_end_event', true)}
                                                menuPortalTarget={document.body}
                                                styles={customStyle}
                                                value={formik.values.eu_port_end_event || null}
                                                noOptionsMessage={() => "Please enter the event name"}
                                            />
                                            {formik.touched.eu_port_end_event && formik.errors.eu_port_end_event && env.form_validation && (
                                                <ErrorTooltip
                                                    target="eu_port_end_event"
                                                    message={formik.errors.eu_port_end_event}
                                                    open={!!formik.errors.eu_port_end_event}
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={4}>
                                            <div className="ele_row1">
                                                <Label htmlFor="is_system_under_maintenance" className="form-check-label me-2 mb-0 mt-1">
                                                    Is system under maintenance?
                                                </Label>
                                                <div className="square-switch">
                                                    <Input
                                                        type="checkbox"
                                                        switch="none"
                                                        name="is_system_under_maintenance"
                                                        id="is_system_under_maintenance"
                                                        defaultChecked={formik?.values?.is_system_under_maintenance}
                                                        onChange={(e: any) => {
                                                            formik?.setFieldValue("is_system_under_maintenance", e.target.checked);
                                                            if (!e.target.checked) {
                                                                formik?.setFieldValue("maintenance_start_time", null);
                                                                formik?.setFieldValue("maintenance_end_time", null);
                                                            }
                                                        }}
                                                    />
                                                    <Label
                                                        htmlFor="is_system_under_maintenance"
                                                        data-on-label="Yes"
                                                        data-off-label="No"
                                                        className="mt-1"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => {
                                                            if (e.key === " " || e.key === "Enter") {
                                                                e.preventDefault();
                                                                document.getElementById("is_system_under_maintenance")?.click();
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                        {formik.values.is_system_under_maintenance && (
                                            <Row>
                                                <Col md={3}>
                                                    <Label className="mb-0 asteric" for="">Maintenance Start time (Local)</Label><br />
                                                    <Input
                                                        onFocus={(e) => e.target.select()}
                                                        name="maintenance_start_time"
                                                        type="datetime-local"
                                                        min={dateTimeFormat(new Date().toISOString())}
                                                        max={"9999-12-31T00:00"}
                                                        className="datetimepicker mt-0 text-uppercase"
                                                        id="maintenance_start_time"
                                                        onBlur={(e: any) => formik?.handleBlur(e)}
                                                        onChange={(e: any) => {
                                                            if (e.target.value) {
                                                                formik?.handleChange(e);
                                                            } else {
                                                                formik?.setFieldValue("maintenance_start_time", null);
                                                            }
                                                        }}
                                                        defaultValue={dateTimeFormat(formik?.values?.maintenance_start_time as string, false, true)}
                                                    />
                                                    {
                                                        formik.touched.maintenance_start_time && formik.errors.maintenance_start_time && env.form_validation &&
                                                        <ErrorTooltip
                                                            target="maintenance_start_time"
                                                            message={formik.errors.maintenance_start_time}
                                                            open={!!formik.errors.maintenance_start_time}
                                                        />
                                                    }
                                                </Col>
                                                <Col md={6}>
                                                    <Label className="mb-0 asteric" for="">Maintenance End time (Local)</Label><br />
                                                    <Input
                                                        onFocus={(e) => e.target.select()}
                                                        name="maintenance_end_time"
                                                        type="datetime-local"
                                                        min={dateTimeFormat(formik?.values?.maintenance_start_time as string, false, true)}
                                                        max={"9999-12-31T00:00"}
                                                        className="datetimepicker mt-0 text-uppercase"
                                                        id="maintenance_end_time"
                                                        onBlur={(e: any) => formik?.handleBlur(e)}
                                                        onChange={(e: any) => {
                                                            if (e.target.value) {
                                                                formik?.handleChange(e);
                                                            } else {
                                                                formik?.setFieldValue("maintenance_end_time", null);
                                                            }
                                                        }}
                                                        defaultValue={dateTimeFormat(formik?.values?.maintenance_end_time as string, false, true)}
                                                    />
                                                    {
                                                        formik.touched.maintenance_end_time && formik.errors.maintenance_end_time && env.form_validation &&
                                                        <ErrorTooltip
                                                            target="maintenance_end_time"
                                                            message={formik.errors.maintenance_end_time}
                                                            open={!!formik.errors.maintenance_end_time}
                                                        />
                                                    }
                                                </Col>
                                            </Row>
                                        )}
                                    </Row>
                                </CardBody>
                                <CardFooter className="p-2 py-3">
                                    <div className="d-flex justify-content-end">
                                        <Button
                                            type="submit"
                                            color="primary"
                                            disabled={formik.isSubmitting}
                                        >
                                            {formik.isSubmitting ? (
                                                <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                            ) : "Save"}
                                        </Button>
                                    </div>
                                </CardFooter>
                                <FormValuesDebug values={[formik.values, formik.errors, formik.initialValues]} />
                            </form>
                        )}
                    </Card>
                </Container>
            </div>
        </React.Fragment >
    );
};

export default OrganizationConfiguration;